import { useState } from "react";

import {
  Typography,
  Stack,
  Box,
  Grid,
  Divider,
  Modal,
  IconButton,
  Paper,
  useMediaQuery,
  Theme,
} from "@mui/material";
import {
  EditTwoTone,
  HighlightOffTwoTone as CloseIcon,
} from "@mui/icons-material";

import PerfectScrollbar from "react-perfect-scrollbar";

import { OrderDeliveryDetailsSkeleton } from "components/skeleton/orderDetailSkeleton";
import MainCard from "components/mainCard";
import Button from "components/button";
import { mainCardSX } from "features/orders/index.styles";
import AddEditReservations from "../addEditReservations";
import { ReservationDetailProps } from "../../index.types";
import { dispatch } from "store";
import { openSnackbar } from "store/slices/snackbar";
import ReservationStatusAction from "../reservationStatusAction";

const ReservationDetail = ({
  branchesList,
  selectedReservation,
  reservationDetailsCallback,
  toggleReservationDetails,
  handleToggleReservationDetails,
}: ReservationDetailProps) => {
  const [toggleAddEditReservations, setToggleAddEditReservations] =
    useState<boolean>(false);

  const isScreenSmall = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("sm")
  );

  //======================================= Handler Functions =======================================//

  const handleToggleAddEditReservations = () => {
    setToggleAddEditReservations((state) => !state);
  };

  const addEditReservationsCallback = () => {
    handleToggleAddEditReservations();

    reservationDetailsCallback();

    // notify user
    dispatch(
      openSnackbar({
        open: true,
        message: "Reservation updated successfully",
        alert: {
          color: "success",
        },
      })
    );
  };

  const reservationStatusActionCallback = () => {
    reservationDetailsCallback();
  };

  return (
    <>
      {toggleAddEditReservations && (
        <AddEditReservations
          branchesList={branchesList}
          selectedReservation={selectedReservation}
          addEditReservationsCallback={addEditReservationsCallback}
          toggleAddEditReservations={toggleAddEditReservations}
          handleToggleAddEditReservations={handleToggleAddEditReservations}
          component={"Modal"}
        />
      )}

      {/* Reservation Detail Modal */}
      <Modal
        open={toggleReservationDetails}
        onClose={handleToggleReservationDetails}
      >
        <Paper>
          <PerfectScrollbar style={{ height: "100vh", overflowX: "hidden" }}>
            <MainCard
              dividerSX={{ m: "unset", display: "none" }}
              headerSX={{ p: "unset !important", mb: "32px" }}
              contentSX={{
                "& .MuiCardContent-root": {
                  p: "unset !important",
                },
                m: "unset",
                p: "unset !important",
              }}
              sx={mainCardSX}
              title={
                <>
                  <Grid container>
                    <Grid item xs={12} sx={{ display: "flex" }}>
                      <Grid
                        item
                        xs={8}
                        sx={{ display: "flex", alignItems: "center" }}
                      >
                        <Typography variant={isScreenSmall ? "h2" : "h1"}>
                          Reservation
                          {` ${selectedReservation[0].reservation_id}`}
                        </Typography>
                      </Grid>

                      <Grid
                        item
                        xs={4}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "end",
                        }}
                      >
                        <Stack
                          sx={{
                            cursor: "pointer",
                            alignItems: "center",
                            ml: "48px",
                          }}
                        >
                          <IconButton
                            sx={{ p: "unset" }}
                            onClick={handleToggleReservationDetails}
                          >
                            <CloseIcon
                              sx={{ color: "#D84315" }}
                              fontSize="large"
                            />
                          </IconButton>
                        </Stack>
                      </Grid>
                    </Grid>
                  </Grid>
                </>
              }
            >
              <Grid container>
                <Grid item xs={12} sx={{ display: { md: "flex" } }}>
                  <Grid item xs={12} md={7.3} lg={8.3}>
                    <Box
                      sx={{
                        border: "1px solid #EEEEEE",
                        borderRadius: "8px",
                        p: "17px 12px 24px 16px",
                        mb: "16px",
                        boxSizing: "border-box",
                      }}
                    >
                      <Stack direction="column">
                        {selectedReservation.length === 0 ? (
                          <OrderDeliveryDetailsSkeleton />
                        ) : (
                          <>
                            {/* reservations details heading stack */}
                            <Stack
                              direction="row"
                              sx={{
                                alignItems: "center",
                                justifyContent: "space-between",
                                mb: "24px",
                              }}
                            >
                              <Stack direction="row">
                                <Typography variant="h4">
                                  Reservation Details
                                </Typography>
                              </Stack>

                              <Stack>
                                <Button
                                  variant="contained"
                                  startIcon={<EditTwoTone />}
                                  sx={{
                                    background: "#F5F5F5 ",
                                    color: "#212121",
                                    p: "9px 20px",

                                    "&:hover": {
                                      backgroundColor: "#F5F5F5",
                                    },
                                  }}
                                  onClick={handleToggleAddEditReservations}
                                >
                                  Edit
                                </Button>
                              </Stack>
                            </Stack>

                            <Divider />

                            <Stack // name stack
                              direction="row"
                              sx={{ mt: "24px" }}
                            >
                              <Typography variant="subtitle1">Name:</Typography>
                              <Typography variant="h5" sx={{ ml: "77px" }}>
                                {selectedReservation[0].user_name}
                              </Typography>
                            </Stack>

                            <Stack // contact stack
                              direction="row"
                              sx={{ mt: "16px" }}
                            >
                              <Typography variant="subtitle1">
                                Contact:
                              </Typography>
                              <Typography variant="h5" sx={{ ml: "65px" }}>
                                {selectedReservation[0].phone}
                              </Typography>
                            </Stack>

                            {selectedReservation[0].email && (
                              <Stack //email Stack
                                direction="row"
                                sx={{ mt: "16px" }}
                              >
                                <Typography variant="subtitle1">
                                  Email:
                                </Typography>
                                <Typography variant="h5" sx={{ ml: "80px" }}>
                                  {selectedReservation[0].email}
                                </Typography>
                              </Stack>
                            )}

                            <Stack // date stack
                              direction="row"
                              sx={{ mt: "16px" }}
                            >
                              <Typography variant="subtitle1">Date:</Typography>
                              <Typography variant="h5" sx={{ ml: "85px" }}>
                                {selectedReservation[0].date}
                              </Typography>
                            </Stack>

                            <Stack // time stack
                              direction="row"
                              sx={{ mt: "16px" }}
                            >
                              <Typography variant="subtitle1">Time:</Typography>
                              <Typography variant="h5" sx={{ ml: "82px" }}>
                                {selectedReservation[0].time}
                              </Typography>
                            </Stack>

                            <Stack // time stack
                              direction="row"
                              sx={{ mt: "16px" }}
                            >
                              <Typography variant="subtitle1">
                                Guests:
                              </Typography>
                              <Typography variant="h5" sx={{ ml: "70px" }}>
                                {selectedReservation[0].guests}
                              </Typography>
                            </Stack>

                            {selectedReservation[0].advance_payment && (
                              <Stack //advance payment Stack
                                direction="row"
                                sx={{ mt: "16px" }}
                              >
                                <Typography variant="subtitle1">
                                  Advance:
                                </Typography>
                                <Typography variant="h5" sx={{ ml: "61px" }}>
                                  {selectedReservation[0].advance_payment}
                                </Typography>
                              </Stack>
                            )}

                            {selectedReservation[0].discount && (
                              <Stack //discount Stack
                                direction="row"
                                sx={{ mt: "16px" }}
                              >
                                <Typography variant="subtitle1">
                                  Discount:
                                </Typography>
                                <Typography variant="h5" sx={{ ml: "60px" }}>
                                  {selectedReservation[0].discount}
                                </Typography>
                              </Stack>
                            )}

                            <Stack // branch stack
                              direction="row"
                              sx={{ mt: "16px" }}
                            >
                              <Typography variant="subtitle1">
                                Branch:
                              </Typography>
                              <Typography variant="h5" sx={{ ml: "71px" }}>
                                {`${selectedReservation[0].address}, ${selectedReservation[0].location}`}
                              </Typography>
                            </Stack>

                            {selectedReservation[0].request && (
                              <Stack //request Stack
                                direction="row"
                                sx={{ mt: "16px" }}
                              >
                                <Typography variant="subtitle1">
                                  Request:
                                </Typography>
                                <Typography variant="h5" sx={{ ml: "64px" }}>
                                  {selectedReservation[0].request}
                                </Typography>
                              </Stack>
                            )}

                            {selectedReservation[0].note && (
                              <Stack //note Stack
                                direction="row"
                                sx={{ mt: "16px" }}
                              >
                                <Typography variant="subtitle1">
                                  Note:
                                </Typography>
                                <Typography variant="h5" sx={{ ml: "85px" }}>
                                  {selectedReservation[0].note}
                                </Typography>
                              </Stack>
                            )}
                          </>
                        )}
                      </Stack>
                    </Box>
                  </Grid>

                  <Grid
                    item
                    xs={12}
                    md={4.7}
                    lg={3.7}
                    sx={{ ml: { md: "19px" } }}
                  >
                    {/* reservation status component */}
                    <ReservationStatusAction
                      reservationId={selectedReservation[0].reservation_id}
                      reservationComment={selectedReservation[0].comment}
                      reservationStatusActionCallback={
                        reservationStatusActionCallback
                      }
                    />
                  </Grid>
                </Grid>
              </Grid>
            </MainCard>
          </PerfectScrollbar>
        </Paper>
      </Modal>
    </>
  );
};

export default ReservationDetail;
